import LogoutIcon from '@mui/icons-material/Logout';
import PsychologyIcon from '@mui/icons-material/Psychology';
import RefreshIcon from '@mui/icons-material/Refresh';
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt';
import {
    AppBar,
    Avatar,
    Box,
    Chip,
    CircularProgress,
    Divider,
    Grow,
    IconButton,
    ListItemIcon,
    Menu,
    MenuItem,
    Tab,
    Tabs,
    Toolbar,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { BFF_BASE_URL } from '../../auth/authApi';
import { useAuth } from '../../auth/AuthGate';
import { RootState } from '../../redux/store';
import { activeNavRoute, navItems, useNavCounts } from '../config/navigation';
import { forceRefreshApp } from '../utils/refreshApp';

import { Logo } from './Logo';
import { Sidebar } from './Sidebar';

export const HEADER_HEIGHT = 64;
export const TABS_HEIGHT = 48;

type HeaderProps = {
    refreshAction?: () => void;
    children?: React.ReactNode;
};

export function Header({ refreshAction, children }: HeaderProps) {
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
    const counts = useNavCounts();
    const { logout, displayName } = useAuth();
    const isLoading = useSelector(
        (state: RootState) => state.loading.activeRequests > 0
    );
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const username = displayName;

    const handleMenuClose = () => setAnchorEl(null);

    const activeTab = activeNavRoute(location.pathname);

    return (
        <Box sx={{ flexGrow: 1, zIndex: 999, display: 'flex', flexDirection: 'column', height: '100vh' }}>
            <AppBar
                position="fixed"
                elevation={0}
                sx={{
                    backgroundColor: theme.palette.background.paper,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    color: theme.palette.text.primary,
                }}
            >
                <Toolbar
                    sx={{
                        justifyContent: 'space-between',
                        minHeight: HEADER_HEIGHT,
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                        <Sidebar />
                        <Logo
                            animate={isLoading}
                            onClick={() => navigate('/')}
                        />
                    </Box>

                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                        }}
                    >
                        {isDesktop && (
                            <Chip
                                size="small"
                                color="success"
                                variant="outlined"
                                label={`● ${BFF_BASE_URL}`}
                            />
                        )}

                        {/* Refresh button and spinner overlap in the same slot */}
                        <Box
                            sx={{ position: 'relative', width: 50, height: 48 }}
                        >
                            <Box
                                sx={{
                                    top: '50%',
                                    left: '50%',
                                    position: 'absolute',
                                    transform: 'translate(-50%, -50%)',
                                    pointerEvents:
                                        !isLoading &&
                                        refreshAction !== undefined
                                            ? 'all'
                                            : 'none',
                                }}
                            >
                                <Grow
                                    in={
                                        !isLoading &&
                                        refreshAction !== undefined
                                    }
                                >
                                    <IconButton
                                        onClick={refreshAction}
                                        color="inherit"
                                        aria-label="Refresh"
                                    >
                                        <RefreshIcon />
                                    </IconButton>
                                </Grow>
                            </Box>
                            <Box
                                sx={{
                                    top: '50%',
                                    left: '50%',
                                    position: 'absolute',
                                    transform: 'translate(-50%, -50%)',
                                    pointerEvents: 'none',
                                }}
                            >
                                <Grow in={isLoading}>
                                    <CircularProgress
                                        color="inherit"
                                        size={24}
                                    />
                                </Grow>
                            </Box>
                        </Box>

                        <Tooltip title="Account settings">
                            <IconButton
                                onClick={(event) =>
                                    setAnchorEl(event.currentTarget)
                                }
                                size="small"
                                sx={{ p: 0.5 }}
                                aria-label="Account settings"
                                aria-controls={open ? 'account-menu' : undefined}
                                aria-haspopup="true"
                                aria-expanded={open ? 'true' : undefined}
                            >
                                <Avatar sx={{ width: 32, height: 32 }}>
                                    {username
                                        ? username.charAt(0).toUpperCase()
                                        : '?'}
                                </Avatar>
                            </IconButton>
                        </Tooltip>
                        <Menu
                            anchorEl={anchorEl}
                            id="account-menu"
                            open={open}
                            onClose={handleMenuClose}
                            onClick={handleMenuClose}
                            PaperProps={{
                                sx: { mt: 1.5, minWidth: '180px' },
                            }}
                            transformOrigin={{
                                horizontal: 'right',
                                vertical: 'top',
                            }}
                            anchorOrigin={{
                                horizontal: 'right',
                                vertical: 'bottom',
                            }}
                        >
                            {username && (
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    noWrap
                                    sx={{ px: 2, py: 1, maxWidth: 260 }}
                                >
                                    {username}
                                </Typography>
                            )}
                            {username && <Divider />}
                            <MenuItem onClick={() => navigate('/profile')}>
                                <ListItemIcon>
                                    <PsychologyIcon fontSize="small" />
                                </ListItemIcon>
                                Memory Profile
                            </MenuItem>
                            <MenuItem onClick={forceRefreshApp}>
                                <ListItemIcon>
                                    <SystemUpdateAltIcon fontSize="small" />
                                </ListItemIcon>
                                Refresh application
                            </MenuItem>
                            <MenuItem onClick={logout}>
                                <ListItemIcon>
                                    <LogoutIcon fontSize="small" />
                                </ListItemIcon>
                                Logout
                            </MenuItem>
                        </Menu>
                    </Box>
                </Toolbar>

                {isDesktop && (
                    <Tabs
                        value={activeTab}
                        onChange={(_, route: string) => navigate(route)}
                        sx={{ px: 2, minHeight: TABS_HEIGHT }}
                    >
                        {navItems.map((item) => {
                            const count = counts[item.route];
                            return (
                                <Tab
                                    key={item.route}
                                    value={item.route}
                                    label={
                                        count === undefined
                                            ? item.text
                                            : `${item.text} (${count})`
                                    }
                                />
                            );
                        })}
                    </Tabs>
                )}
            </AppBar>
            {children}
        </Box>
    );
}
